/**
 * Admin Course Edit Page - Create/edit course with exam and questions.
 */

import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "../../../components/layout/index.ts";
import { PageLoader } from "../../../components/PageLoader.tsx";
import {
  IconAlertTriangle,
  IconAcademicCap,
  IconTrash,
  IconPlus,
  IconSave,
  IconCheck,
  IconChevronRight,
  IconCamera,
  IconPhoto,
  IconShare,
} from "../../../components/Icons.tsx";
import { getTelegram, haptic, safeShowAlert } from "../../../lib/telegram.ts";
import { getAuthHeaders, getErrorMessage } from "../../../lib/graphql.ts";
import { config } from "../../../config.ts";
import { safeParseResponseJson } from "../../../lib/json-utils.ts";
import {
  useAdminCourse,
  useAdminExamQuestions,
  useCreateCourse,
  useUpdateCourse,
  useDeleteCourse,
  useCreateExam,
  useUpdateExam,
  useCreateQuestion,
  useDeleteQuestion,
} from "./hooks";
import type { AdminExamQuestion, CreateCourseInput, UpdateCourseInput } from "./hooks";
import { QuestionEditor } from "./components/QuestionEditor";
import { ShareCourseSheet } from "./components/ShareCourseSheet";

// =============================================================================
// FORM STATE
// =============================================================================

interface CourseFormData {
  externalId: string;
  title: string;
  description: string;
  externalUrl: string;
  imageUrl: string;
  sortOrder: number;
  isActive: boolean;
}

interface ExamFormData {
  name: string;
  description: string;
  passingScore: number;
}

const defaultCourseForm: CourseFormData = {
  externalId: "",
  title: "",
  description: "",
  externalUrl: "",
  imageUrl: "",
  sortOrder: 0,
  isActive: true,
};

const defaultExamForm: ExamFormData = {
  name: "Контрольные вопросы",
  description: "",
  passingScore: 100,
};

// =============================================================================
// QUESTION LIST
// =============================================================================

interface QuestionListProps {
  questions: AdminExamQuestion[];
  onEdit: (question: AdminExamQuestion) => void;
  onDelete: (id: string) => void;
  isDeleting: boolean;
}

function QuestionList({ questions, onEdit, onDelete, isDeleting }: QuestionListProps) {
  if (questions.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-slate-500">
        Нет вопросов. Добавьте первый вопрос.
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100">
      {questions.map((q, idx) => (
        <div
          key={q.id}
          className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
        >
          <span className="w-6 h-6 rounded-full bg-slate-100 text-slate-500 text-xs font-medium flex items-center justify-center flex-shrink-0">
            {idx + 1}
          </span>
          <button
            type="button"
            onClick={() => onEdit(q)}
            className="flex-1 min-w-0 text-left"
          >
            <p className="text-[13px] text-slate-900 line-clamp-2">{q.text}</p>
            <p className="text-[11px] text-slate-400 mt-0.5">
              {q.options.length} вариантов · Ответ: {q.correctAnswer}
            </p>
          </button>
          <button
            type="button"
            onClick={() => {
              haptic("light");
              onDelete(q.id);
            }}
            disabled={isDeleting}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
          >
            <IconTrash className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export function AdminCourseEditPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();

  // If courseId is undefined (from /admin/academy/new route) or "new", it's a new course
  const isNew = !courseId || courseId === "new";

  // Data fetching
  const { course, isLoading: isLoadingCourse, error: courseError, refetch } = useAdminCourse(
    isNew ? "" : courseId ?? ""
  );

  const examId = course?.exam?.id ?? "";
  const { questions, isLoading: isLoadingQuestions, refetch: refetchQuestions } =
    useAdminExamQuestions(examId);

  // Mutations
  const { createCourse, isLoading: isCreating } = useCreateCourse();
  const { updateCourse, isLoading: isUpdating } = useUpdateCourse();
  const { deleteCourse, isLoading: isDeleting } = useDeleteCourse();
  const { createExam, isLoading: isCreatingExam } = useCreateExam();
  const { updateExam, isLoading: isUpdatingExam } = useUpdateExam();
  const { isLoading: isCreatingQuestion } = useCreateQuestion();
  const { deleteQuestion, isLoading: isDeletingQuestion } = useDeleteQuestion();

  // Form state
  const [courseForm, setCourseForm] = useState<CourseFormData>(defaultCourseForm);
  const [examForm, setExamForm] = useState<ExamFormData>(defaultExamForm);
  const [editingQuestion, setEditingQuestion] = useState<AdminExamQuestion | null>(null);
  const [showQuestionEditor, setShowQuestionEditor] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Image upload state
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [imageCacheBust, setImageCacheBust] = useState<number | null>(null);

  // Share sheet state
  const [showShareSheet, setShowShareSheet] = useState(false);

  // Initialize form from course data
  useEffect(() => {
    if (course) {
      setCourseForm({
        externalId: course.externalId ?? "",
        title: course.title,
        description: course.description ?? "",
        externalUrl: course.externalUrl ?? "",
        imageUrl: course.imageUrl ?? "",
        sortOrder: course.sortOrder,
        isActive: course.isActive,
      });
      if (course.exam) {
        setExamForm({
          name: course.exam.name,
          description: course.exam.description ?? "",
          passingScore: course.exam.passingScore,
        });
      }
    }
  }, [course]);

  // Back button
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (showQuestionEditor) {
        setShowQuestionEditor(false);
        setEditingQuestion(null);
      } else {
        navigate("/admin/academy");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate, showQuestionEditor]);

  // Handlers
  const handleSaveCourse = useCallback(async () => {
    setSaveError(null);
    haptic("light");

    try {
      if (isNew) {
        const input: CreateCourseInput = {
          externalId: courseForm.externalId || undefined,
          title: courseForm.title,
          description: courseForm.description || undefined,
          externalUrl: courseForm.externalUrl || undefined,
          imageUrl: courseForm.imageUrl || undefined,
          sortOrder: courseForm.sortOrder,
        };
        const newCourse = await createCourse(input);
        if (newCourse) {
          haptic("success");
          navigate(`/admin/academy/${newCourse.id}`, { replace: true });
        }
      } else if (courseId) {
        const input: UpdateCourseInput = {
          externalId: courseForm.externalId || null,
          title: courseForm.title,
          description: courseForm.description || null,
          externalUrl: courseForm.externalUrl || null,
          imageUrl: courseForm.imageUrl || null,
          sortOrder: courseForm.sortOrder,
          isActive: courseForm.isActive,
        };
        await updateCourse(courseId, input);
        haptic("success");
        refetch();
      }
    } catch (err) {
      haptic("error");
      setSaveError(getErrorMessage(err, "Не удалось сохранить"));
    }
  }, [isNew, courseForm, createCourse, updateCourse, courseId, refetch, navigate]);

  const handleCreateExam = useCallback(async () => {
    if (!courseId || isNew) return;
    setSaveError(null);
    haptic("light");

    try {
      await createExam({
        courseId,
        name: examForm.name,
        description: examForm.description || undefined,
        passingScore: examForm.passingScore,
      });
      haptic("success");
      refetch();
    } catch (err) {
      haptic("error");
      setSaveError(getErrorMessage(err, "Не удалось создать тест"));
    }
  }, [courseId, isNew, examForm, createExam, refetch]);

  const handleUpdateExam = useCallback(async () => {
    if (!examId) return;
    setSaveError(null);
    haptic("light");

    try {
      await updateExam(examId, {
        name: examForm.name,
        description: examForm.description || null,
        passingScore: examForm.passingScore,
      });
      haptic("success");
      refetch();
    } catch (err) {
      haptic("error");
      setSaveError(getErrorMessage(err, "Не удалось обновить тест"));
    }
  }, [examId, examForm, updateExam, refetch]);

  const handleDeleteCourse = useCallback(async () => {
    if (!courseId || isNew) return;
    if (!window.confirm("Удалить курс? Это действие необратимо.")) return;

    haptic("warning");
    try {
      await deleteCourse(courseId);
      haptic("success");
      navigate("/admin/academy", { replace: true });
    } catch (err) {
      haptic("error");
      setSaveError(getErrorMessage(err, "Не удалось удалить"));
    }
  }, [courseId, isNew, deleteCourse, navigate]);

  const handleAddQuestion = useCallback(() => {
    haptic("light");
    setEditingQuestion(null);
    setShowQuestionEditor(true);
  }, []);

  const handleEditQuestion = useCallback((question: AdminExamQuestion) => {
    haptic("light");
    setEditingQuestion(question);
    setShowQuestionEditor(true);
  }, []);

  const handleDeleteQuestion = useCallback(
    async (id: string) => {
      if (!window.confirm("Удалить вопрос?")) return;
      haptic("warning");

      try {
        await deleteQuestion(id);
        haptic("success");
        refetchQuestions();
      } catch (err) {
        haptic("error");
        setSaveError(getErrorMessage(err, "Не удалось удалить"));
      }
    },
    [deleteQuestion, refetchQuestions]
  );

  const handleQuestionSaved = useCallback(() => {
    setShowQuestionEditor(false);
    setEditingQuestion(null);
    refetchQuestions();
  }, [refetchQuestions]);

  const handleImageUpload = useCallback(async (file: File) => {
    if (!courseId || isNew) return;
    setIsUploadingImage(true);
    setSaveError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(
        `${config.apiUrl}/upload/academy-course-image/${courseId}`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: formData,
        },
      );
      if (!response.ok) {
        const data = await safeParseResponseJson<{ error?: string }>(response, "course image upload");
        throw new Error(data.error || "Ошибка загрузки");
      }
      const result = await response.json() as { url: string };
      setCourseForm((prev) => ({ ...prev, imageUrl: result.url }));
      setImageCacheBust(Date.now());
      haptic("success");
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Ошибка загрузки изображения"));
    } finally {
      setIsUploadingImage(false);
      if (imageInputRef.current) imageInputRef.current.value = "";
    }
  }, [courseId, isNew]);

  const handleRemoveImage = useCallback(() => {
    setCourseForm((prev) => ({ ...prev, imageUrl: "" }));
  }, []);

  // Loading state
  if (!isNew && isLoadingCourse) {
    return <PageLoader message="Загрузка курса..." />;
  }

  // Error state
  if (courseError) {
    return (
      <Layout>
        <div className="card p-5 text-center">
          <IconAlertTriangle className="w-12 h-12 mx-auto mb-3 text-amber-500" />
          <h2 className="text-lg font-bold text-slate-900 mb-2">Ошибка</h2>
          <p className="text-sm text-slate-500 mb-4">
            {getErrorMessage(courseError, "Не удалось загрузить курс")}
          </p>
          <button onClick={() => refetch()} className="btn-primary">
            Попробовать снова
          </button>
        </div>
      </Layout>
    );
  }

  // Question editor overlay
  if (showQuestionEditor && examId) {
    return (
      <Layout>
        <QuestionEditor
          examId={examId}
          question={editingQuestion}
          onSave={handleQuestionSaved}
          onCancel={() => {
            setShowQuestionEditor(false);
            setEditingQuestion(null);
          }}
        />
      </Layout>
    );
  }

  const isSaving = isCreating || isUpdating || isCreatingExam || isUpdatingExam;

  return (
    <Layout>
      <div className="pb-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
          <IconAcademicCap className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-slate-900">
            {isNew ? "Новый курс" : "Редактирование курса"}
          </h1>
          {!isNew && course?.title && (
            <p className="text-[13px] text-slate-500 truncate max-w-[200px] xs:max-w-[280px]">
              {course.title}
            </p>
          )}
        </div>
        {!isNew && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => {
                haptic("light");
                setShowShareSheet(true);
              }}
              className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center active:scale-95 transition-transform"
              aria-label="Поделиться"
            >
              <IconShare className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={handleDeleteCourse}
              disabled={isDeleting}
              className="w-10 h-10 rounded-xl bg-red-50 text-red-500 flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
              aria-label="Удалить курс"
            >
              <IconTrash className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* Error banner */}
      {saveError && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-100">
          <p className="text-sm text-red-600">{saveError}</p>
        </div>
      )}

      {/* Course form */}
      <section className="mb-6">
        <h2 className="text-[15px] font-semibold text-slate-900 mb-3">
          Основная информация
        </h2>
        <div className="card p-4 space-y-4">
          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1">
              Название курса *
            </label>
            <input
              type="text"
              value={courseForm.title}
              onChange={(e) =>
                setCourseForm((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="Обнаружение своих учеников"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1">
              UUID курса (externalId)
            </label>
            <input
              type="text"
              value={courseForm.externalId}
              onChange={(e) =>
                setCourseForm((prev) => ({ ...prev, externalId: e.target.value }))
              }
              placeholder="auto-generated if empty"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[15px] font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              Используется для связи с one-academy
            </p>
          </div>

          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1">
              Описание
            </label>
            <textarea
              value={courseForm.description}
              onChange={(e) =>
                setCourseForm((prev) => ({ ...prev, description: e.target.value }))
              }
              placeholder="Краткое описание курса"
              rows={2}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 resize-none"
            />
          </div>

          <div>
            <label className="block text-[13px] font-medium text-slate-700 mb-1">
              Ссылка на курс *
            </label>
            <input
              type="url"
              value={courseForm.externalUrl}
              onChange={(e) =>
                setCourseForm((prev) => ({ ...prev, externalUrl: e.target.value }))
              }
              placeholder="https://www.bible-life.academy/courses/..."
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>

          {/* Image upload - only for existing courses */}
          {!isNew && (
            <div>
              <label className="block text-[13px] font-medium text-slate-700 mb-2">
                Обложка
              </label>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleImageUpload(file);
                }}
                className="hidden"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isUploadingImage) imageInputRef.current?.click();
                }}
                disabled={isUploadingImage}
                className="relative w-full aspect-[2/1] rounded-xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-200 active:border-slate-300 transition-colors disabled:opacity-90 flex items-center justify-center"
              >
                {courseForm.imageUrl && !isUploadingImage ? (
                  <>
                    <img
                      src={
                        imageCacheBust
                          ? `${courseForm.imageUrl}${courseForm.imageUrl.includes("?") ? "&" : "?"}_t=${imageCacheBust}`
                          : courseForm.imageUrl
                      }
                      alt="Обложка курса"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <span className="absolute bottom-0 left-0 right-0 py-2 px-3 bg-black/50 text-white text-[13px] font-medium flex items-center justify-center gap-2">
                      <IconCamera className="w-4 h-4" />
                      Заменить
                    </span>
                  </>
                ) : isUploadingImage ? (
                  <div className="flex flex-col items-center justify-center gap-2 text-slate-500">
                    <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                    <span className="text-[13px]">Загрузка…</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                    <IconPhoto className="w-10 h-10" />
                    <span className="text-[13px]">Добавить обложку</span>
                  </div>
                )}
              </button>
              {courseForm.imageUrl && !isUploadingImage && (
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  className="mt-2 flex items-center gap-1 text-[13px] text-red-500 active:text-red-600"
                >
                  <IconTrash className="w-4 h-4" />
                  Удалить
                </button>
              )}
            </div>
          )}

          {/* Note for new courses */}
          {isNew && (
            <p className="text-[12px] text-slate-400">
              Сохраните курс, чтобы добавить обложку
            </p>
          )}

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-[13px] font-medium text-slate-700 mb-1">
                Порядок
              </label>
              <input
                type="number"
                value={courseForm.sortOrder}
                onChange={(e) =>
                  setCourseForm((prev) => ({
                    ...prev,
                    sortOrder: parseInt(e.target.value, 10) || 0,
                  }))
                }
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[15px] focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer p-2">
                <input
                  type="checkbox"
                  checked={courseForm.isActive}
                  onChange={(e) =>
                    setCourseForm((prev) => ({ ...prev, isActive: e.target.checked }))
                  }
                  className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-[13px] text-slate-700">Активен</span>
              </label>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSaveCourse}
            disabled={isSaving || !courseForm.title}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed active:scale-[0.99] transition-transform"
          >
            {isSaving ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <IconSave className="w-4 h-4" />
                {isNew ? "Создать курс" : "Сохранить"}
              </>
            )}
          </button>
        </div>
      </section>

      {/* Exam section - only for existing courses */}
      {!isNew && (
        <section className="mb-6">
          <h2 className="text-[15px] font-semibold text-slate-900 mb-3">
            Контрольные вопросы
          </h2>

          {!course?.exam ? (
            <div className="card p-4">
              <p className="text-[13px] text-slate-500 mb-3">
                Тест еще не создан. Создайте тест, чтобы добавить вопросы.
              </p>
              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-[13px] font-medium text-slate-700 mb-1">
                    Название теста
                  </label>
                  <input
                    type="text"
                    value={examForm.name}
                    onChange={(e) =>
                      setExamForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[15px]"
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-medium text-slate-700 mb-1">
                    Проходной балл (%)
                  </label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={examForm.passingScore}
                    onChange={(e) =>
                      setExamForm((prev) => ({
                        ...prev,
                        passingScore: parseInt(e.target.value, 10) || 100,
                      }))
                    }
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[15px]"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={handleCreateExam}
                disabled={isCreatingExam}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium disabled:opacity-50"
              >
                <IconPlus className="w-4 h-4" />
                Создать тест
              </button>
            </div>
          ) : (
            <div className="card overflow-hidden">
              {/* Exam settings */}
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-[14px] font-semibold text-slate-900">
                    {course.exam.name}
                  </h3>
                  <span className="px-2 py-0.5 text-[11px] font-medium text-emerald-600 bg-emerald-50 rounded">
                    {course.exam.questionsCount} вопросов
                  </span>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={examForm.name}
                      onChange={(e) =>
                        setExamForm((prev) => ({ ...prev, name: e.target.value }))
                      }
                      placeholder="Название теста"
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[14px]"
                    />
                  </div>
                  <div className="w-20">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={examForm.passingScore}
                      onChange={(e) =>
                        setExamForm((prev) => ({
                          ...prev,
                          passingScore: parseInt(e.target.value, 10) || 100,
                        }))
                      }
                      className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[14px]"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleUpdateExam}
                    disabled={isUpdatingExam}
                    className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-[13px] font-medium disabled:opacity-50"
                  >
                    <IconCheck className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Questions list */}
              {isLoadingQuestions ? (
                <div className="p-4 text-center text-sm text-slate-500">
                  Загрузка вопросов...
                </div>
              ) : (
                <QuestionList
                  questions={questions}
                  onEdit={handleEditQuestion}
                  onDelete={handleDeleteQuestion}
                  isDeleting={isDeletingQuestion}
                />
              )}

              {/* Add question button */}
              <div className="p-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={handleAddQuestion}
                  disabled={isCreatingQuestion}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-50 text-blue-600 font-medium text-[13px] active:scale-[0.99] transition-transform"
                >
                  <IconPlus className="w-4 h-4" />
                  Добавить вопрос
                </button>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Analytics link */}
      {!isNew && (
        <button
          type="button"
          onClick={() => {
            haptic("light");
            navigate(`/admin/academy/${courseId}/analytics`);
          }}
          className="w-full flex items-center gap-3 p-4 rounded-xl bg-white border border-slate-100 shadow-sm text-left"
        >
          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center text-amber-600">
            <IconAcademicCap className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <p className="text-[14px] font-semibold text-slate-900">Аналитика курса</p>
            <p className="text-[12px] text-slate-500">
              Посещения, прохождения, статистика
            </p>
          </div>
          <IconChevronRight className="w-5 h-5 text-slate-400" />
        </button>
      )}

      <div className="h-8" />
      </div>

      {/* Share sheet */}
      {!isNew && courseId && showShareSheet && (
        <ShareCourseSheet
          open={showShareSheet}
          onOpenChange={setShowShareSheet}
          courseId={courseId}
          courseTitle={course?.title ?? ""}
        />
      )}
    </Layout>
  );
}
